import {lazy, Suspense, useContext, useEffect, useState} from 'react'

import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import PanelBasic from '@adv-mt/mt-panel-basic'
import SectionBasic from '@adv-mt/mt-section-basic'
import SpinnerBasic, {spinnerBasicOverlayTypes, spinnerBasicTypes} from '@adv-mt/mt-spinner-basic'

const PanelFinancing = lazy(() =>
  import('@adv-mt/mt-panel-financing').then(module => ({default: module.PanelFinancing}))
)

export const DetailPanelFinancingLazy = ({
  ad,
  seller,
  baseFinancingData,
  showC2cFinancing,
  showB2cFinancingPanel,
  vehicleInfo,
  id,
  onCalculatorChange,
  onContactSellerCtaClick
}) => {
  const {domain, i18n} = useContext(Context)
  const {TransactionNoContentError} = domain.get('config').get('COMMON').ERRORS
  const [financingData, setFinancingData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  const getC2cLoanCalculations = () => {
    const {lender, minEntry, terms} = baseFinancingData
    const {plateRegistrationDate, price, year} = ad
    return domain.get('get_loan_calculations_transactions_use_case').execute({
      carPlateRegistrationDate: plateRegistrationDate || `01/${year}`,
      lender,
      openingFee: minEntry,
      price,
      terms
    })
  }

  const getB2cLoanCalculations = () => {
    const {lender, minEntry, terms} = baseFinancingData
    return domain.get('get_loan_calculations_B2C_transactions_use_case').execute({
      adId: ad.id,
      entry: minEntry,
      lender,
      terms,
      withLifeInsurance: false
    })
  }

  const getPanelFinancingData = async () => {
    if (showC2cFinancing) {
      const [error, response] = await getC2cLoanCalculations()
      if (error || !response) return null
      return {...baseFinancingData, initialLoanCalculations: response}
    }

    if (showB2cFinancingPanel) {
      const [error, response] = await getB2cLoanCalculations()
      if (error instanceof TransactionNoContentError) return {...baseFinancingData}
      if (error || !response) return null
      return {...baseFinancingData, calculatorB2cData: response}
    }

    return {...baseFinancingData}
  }

  const fetchFinancingCalculations = async () => {
    const panelFinancingData = await getPanelFinancingData()
    setFinancingData(panelFinancingData)
    setHasError(!panelFinancingData)
    setIsLoading(false)
  }

  useEffect(() => {
    fetchFinancingCalculations()
  }, [ad.id]) // eslint-disable-line react-hooks/exhaustive-deps

  if (hasError) {
    return null
  }

  if (isLoading) {
    return (
      <SectionBasic id={id}>
        <PanelBasic>
          <div className="mt-DetailPanelFinancing-loading">
            <SpinnerBasic
              inheritColorText
              overlayType={spinnerBasicOverlayTypes.LIGHT}
              type={spinnerBasicTypes.SECTION}
              text={i18n.t('COMMON.LOADING')}
            />
          </div>
        </PanelBasic>
      </SectionBasic>
    )
  }
  return (
    <Suspense
      fallback={
        <SectionBasic id={id}>
          <PanelBasic>
            <div className="mt-DetailPanelFinancing-loading">
              <SpinnerBasic
                inheritColorText
                overlayType={spinnerBasicOverlayTypes.LIGHT}
                type={spinnerBasicTypes.SECTION}
                text={i18n.t('COMMON.LOADING')}
              />
            </div>
          </PanelBasic>
        </SectionBasic>
      }
    >
      <PanelFinancing
        ad={ad}
        seller={seller}
        financingData={financingData}
        id={id}
        onCalculatorChange={onCalculatorChange}
        onContactSellerCtaClick={onContactSellerCtaClick}
        vehicleInfo={vehicleInfo}
      />
    </Suspense>
  )
}

DetailPanelFinancingLazy.propTypes = {
  ad: PropTypes.object.isRequired,
  seller: PropTypes.object.isRequired,
  baseFinancingData: PropTypes.object,
  showC2cFinancing: PropTypes.bool,
  showB2cFinancingPanel: PropTypes.bool,
  vehicleInfo: PropTypes.object,
  id: PropTypes.string,
  onCalculatorChange: PropTypes.func,
  onContactSellerCtaClick: PropTypes.func
}

DetailPanelFinancingLazy.defaultProps = {
  baseFinancingData: null,
  showC2cFinancing: false,
  showB2cFinancingPanel: false,
  vehicleInfo: {},
  onCalculatorChange: () => {}
}

export default DetailPanelFinancingLazy
